import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import {
  CartDispensary,
  DeliveryServiceArea,
} from "@/custom-types/CartDispensary";
import {
  ScheduleStatuses,
  WeeklySchedule,
} from "@/custom-types/DispensarySchedules";
import { HYDRATE, type HydrateAction } from "@/redux/reducers/hydration";
import { clearSessionStorage } from "@/utils/sessionStorage";

type CartDispensaryState = {
  scheduleStatuses: {
    currentStatuses: ScheduleStatuses;
    schedules: WeeklySchedule;
  } | null;
  data: CartDispensary | null;
  deliveryServiceArea: DeliveryServiceArea | null;
  deliveryServiceAreaLoading: boolean;
  menuLink: string | null;
  checkoutDisclaimer: string | null;
};

export const initialState: CartDispensaryState = {
  checkoutDisclaimer: null,
  data: null,
  deliveryServiceArea: null,
  deliveryServiceAreaLoading: false,
  menuLink: null,
  scheduleStatuses: null,
};

const cartDispensarySlice = createSlice({
  extraReducers: (builder) => {
    builder.addCase(HYDRATE, (state, action: HydrateAction) => {
      /**
       * The `cartDispensary` state is modified in `getServerSideProps` of checkout
       * pages, and needs to be loaded here to ensure that data is added to the
       * redux state.
       */
      return {
        /**
         * Retain any state that was added client-side that isn't in the initial state.
         */
        ...state,
        ...action.payload.cartDispensary,
      };
    });
  },
  initialState,
  name: "cartDispensary",
  reducers: {
    loadCartDispensary: (
      state,
      action: PayloadAction<CartDispensary | null>,
    ) => {
      state.data = action.payload;
    },
    loadCheckoutDisclaimer: (state, action: PayloadAction<string | null>) => {
      state.checkoutDisclaimer = action.payload;
    },
    setDeliveryServiceArea: (
      state,
      action: PayloadAction<DeliveryServiceArea | null>,
    ) => {
      state.deliveryServiceArea = action.payload;
    },
    setMenuLink: (state, action: PayloadAction<string | null>) => {
      if (!action.payload) {
        clearSessionStorage("referrer");
      }
      state.menuLink = action.payload;
    },
    setScheduleStatuses: (
      state,
      action: PayloadAction<{
        currentStatuses: ScheduleStatuses;
        schedules: WeeklySchedule;
      } | null>,
    ) => {
      state.scheduleStatuses = action.payload;
    },
  },
});

export const {
  loadCartDispensary,
  loadCheckoutDisclaimer,
  setScheduleStatuses,
  setDeliveryServiceArea,
  setMenuLink,
} = cartDispensarySlice.actions;

export default cartDispensarySlice.reducer;
